import React, { useState } from 'react';
import { Modal, Input, Button, message, Table } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

interface ParsedSamagri {
  name: string;
  desc: string;
  alternativeNames: string[];
}

interface SamagriParserProps {
  visible: boolean;
  onCancel: () => void;
  onImport: (samagris: ParsedSamagri[]) => void;
}

const SamagriParser: React.FC<SamagriParserProps> = ({ visible, onCancel, onImport }) => {
  const [inputText, setInputText] = useState('');
  const [parsedData, setParsedData] = useState<ParsedSamagri[]>([]);

  const parseText = () => {
    try {
      const lines = inputText.trim().split('\n').filter(line => line.trim());
      const result: ParsedSamagri[] = [];

      for (let i = 0; i < lines.length; i += 3) {
        if (i + 2 < lines.length) {
          const name = lines[i].trim();
          const desc = lines[i + 1].trim();
          const altNames = lines[i + 2].split(',').map(n => n.trim()).filter(n => n);

          result.push({ name, desc, alternativeNames: altNames});
        }
      }

      setParsedData(result);
      message.success(`Parsed ${result.length} samagris`);
    } catch (error) {
      message.error('Failed to parse text');
    }
  };

  const handleImport = () => {
    if (parsedData.length === 0) {
      message.warning('No data to import');
      return;
    }
    onImport(parsedData);
    setInputText('');
    setParsedData([]);
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Description', dataIndex: 'desc', key: 'desc' },
    { title: 'Alt Names', dataIndex: 'alternativeNames', key: 'alt', render: (names: string[]) => names.join(', ') },
  ];

  return (
    <Modal
      title="Import Samagris"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="parse" onClick={parseText}>Parse</Button>,
        <Button key="import" type="primary" onClick={handleImport} disabled={parsedData.length === 0}>
          Import {parsedData.length > 0 && `(${parsedData.length})`}
        </Button>,
      ]}
    >
      <Input.TextArea
        rows={10}
        placeholder="Paste data (3 lines per samagri: name, description, alternative names/tags)"
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      {parsedData.length > 0 && (
        <Table
          dataSource={parsedData}
          columns={columns}
          rowKey="name"
          pagination={false}
          size="small"
          scroll={{ y: 300 }}
        />
      )}
    </Modal>
  );
};

export default SamagriParser;
